import React, { useState } from 'react';
import './ImageUpload.css';
import { API_BASE } from '../config/api';

const ImageUpload = ({ player, round, matchId, playerNumber, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Show warning if context is missing
  const hasContext = round && matchId && playerNumber;
  if (!hasContext) {
    return (
      <div className="image-upload-container">
        <p className="upload-warning">
          ⚠️ To upload a photo, please click on the player from a match card or match details.
        </p>
      </div>
    );
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setError('');
      setSuccess('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !player || !round || !matchId || !playerNumber) {
      setError('Missing required information');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Upload the image file
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('playerName', player.name);

      const uploadResponse = await fetch(`${API_BASE}/player/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const uploadData = await uploadResponse.json();

      if (!uploadResponse.ok) {
        throw new Error(uploadData.error || 'Failed to upload image');
      }

      // Step 2: Update the player's image path in tournament data
      const updateResponse = await fetch(`${API_BASE}/player/update-image`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          playerName: player.name,
          imagePath: uploadData.imagePath,
          round: round,
          matchId: matchId,
          playerNumber: playerNumber
        }),
      });

      const updateData = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(updateData.error || 'Failed to update player image');
      }

      setSuccess('Image uploaded and updated successfully!');
      setSelectedFile(null);
      setPreview(null);

      // Call success callback to refresh data
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 1000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="image-upload-container">
      <h3 className="image-upload-title">Upload Player Photo</h3>
      
      {!selectedFile ? (
        <div className="image-upload-input-wrapper">
          <label htmlFor={`file-input-${playerNumber}`} className="image-upload-label">
            <span className="upload-icon">📷</span>
            <span>Choose Image</span>
          </label>
          <input
            id={`file-input-${playerNumber}`}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="image-upload-input"
          />
        </div>
      ) : (
        <div className="image-upload-preview">
          <div className="preview-image-wrapper">
            <img src={preview} alt="Preview" className="preview-image" />
          </div>
          <div className="preview-actions">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="upload-button"
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
            <button
              onClick={handleCancel}
              disabled={uploading}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <div className="upload-error">{error}</div>}
      {success && <div className="upload-success">{success}</div>}
    </div>
  );
};

export default ImageUpload;
